import React, { Ref, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  TextInputProps,
  View,
} from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXSText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface VXLTextInputProps
  extends TextInputProps, PressableAnalyticsProps {
  ref?: Ref<TextInput>;
  /** Display an icon on the left side of the input. */
  leftIcon?: InputIconProps;
  /** Display an icon on the right side of the input. */
  rightIcon?: InputIconProps;
  /** Label text that sits above the input. */
  labelText?: string;
  /** Text that provides additional context or information about the input. */
  assistiveText?: string;
  /** Text that displays the field in an error state when provided. Providing this applies the error styling to the component.*/
  errorText?: string;
  /** Optional inline value suggestion applied through onChangeText when selected. */
  suggestion?: {
    suggestedValue: string;
    onApply?: (suggestedValue: string) => void;
    accessibilityLabel?: string;
    accessibilityHint?: string;
  };
}

interface SupportingContentProps {
  assistiveText?: string;
  errorText?: string;
  isDisabled: boolean | undefined;
  onChangeText?: TextInputProps['onChangeText'];
  suggestion?: VXLTextInputProps['suggestion'];
}

interface InputIconProps {
  iconKey: IconKeyType;
  iconColor?: string;
  iconAccessibilityLabel?: string;
  onPress?: () => void;
}

const InputIcon = ({
  iconKey,
  iconColor,
  iconAccessibilityLabel,
  onPress,
}: InputIconProps) => {
  const iconElement = (
    <Icon
      iconKey={iconKey}
      iconColor={iconColor}
      iconAccessibilityLabel={onPress ? undefined : iconAccessibilityLabel}
      iconSize="MD"
    />
  );

  if (onPress) {
    return (
      <PressableWithDelay
        onPress={onPress}
        role="button"
        accessibilityLabel={iconAccessibilityLabel}
        hitSlop={40}
      >
        {iconElement}
      </PressableWithDelay>
    );
  }

  return iconElement;
};

const SupportingContent = ({
  assistiveText,
  errorText,
  isDisabled,
  onChangeText,
  suggestion,
}: SupportingContentProps) => {
  const appliedOnPressInValueRef = useRef<string | null>(null);

  const applySuggestion = () => {
    if (!suggestion) {
      return;
    }

    onChangeText?.(suggestion.suggestedValue);
    suggestion.onApply?.(suggestion.suggestedValue);
  };

  const handleSuggestionPressIn = () => {
    if (Platform.OS === 'web' || !suggestion) {
      return;
    }

    appliedOnPressInValueRef.current = suggestion.suggestedValue;
    applySuggestion();
  };

  const handleSuggestionPress = () => {
    if (
      suggestion &&
      appliedOnPressInValueRef.current === suggestion.suggestedValue
    ) {
      appliedOnPressInValueRef.current = null;
      return;
    }

    applySuggestion();
  };

  if (errorText) {
    return (
      <View style={styles.supportingRow} role="alert">
        <Icon
          iconColor={theme.color.status.negative}
          iconKey="STATUS_ERROR"
          iconSize="SM"
        />
        <BodyXSText
          numberOfLines={1}
          style={[styles.supportingText, styles.errorSupportingText]}
        >
          {errorText}
        </BodyXSText>
      </View>
    );
  }
  if (suggestion && !isDisabled) {
    return (
      <View
        style={styles.suggestionContainer}
        // On web, pressing the suggestion would otherwise blur the input first,
        // triggering onBlur validation that can hide the suggestion (and its
        // selection handler) before the click resolves. Preventing the default
        // mousedown keeps focus on the input so the suggestion can apply.
        {...(Platform.OS === 'web' && {
          onMouseDown: (event: { preventDefault: () => void }) =>
            event.preventDefault(),
        })}
      >
        <Icon iconKey="STATUS_INFO" iconSize="SM" />
        <BodyXSText numberOfLines={1}>Did you mean</BodyXSText>
        <Pressable
          accessibilityRole="button"
          accessibilityHint="Replace the input with the suggested text"
          accessibilityLabel={
            suggestion.accessibilityLabel ??
            `Accept input suggestion: ${suggestion.suggestedValue}`
          }
          hitSlop={theme.spacing.s}
          onPress={handleSuggestionPress}
          onPressIn={handleSuggestionPressIn}
          role="button"
        >
          <HyperlinkXSText accessible={false} numberOfLines={1}>
            {suggestion.suggestedValue}
          </HyperlinkXSText>
        </Pressable>
        <BodyXSText>?</BodyXSText>
      </View>
    );
  }
  if (assistiveText) {
    return (
      <View style={styles.supportingRow}>
        <BodyXSText
          numberOfLines={1}
          style={[
            styles.supportingText,
            isDisabled ? styles.disabledSupportingText : null,
          ]}
        >
          {assistiveText}
        </BodyXSText>
      </View>
    );
  }

  return null;
};

const VXLTextInput = ({
  ref,
  leftIcon,
  rightIcon,
  assistiveText = '',
  labelText = '',
  errorText = '',
  suggestion,
  analyticsId = '',
  customAttributes = null,
  onFocus,
  onBlur,
  ...props
}: VXLTextInputProps) => {
  const textInputAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });
  const [isFocused, setIsFocused] = useState(false);
  const isError = errorText.length > 0;

  return (
    <View style={styles.container}>
      {labelText.length > 0 && (
        <BodySmBoldText nativeID={labelText}>{labelText}</BodySmBoldText>
      )}
      <View
        style={[
          styles.inputContainer,
          isFocused ? styles.focusedOutline : null,
          isError ? styles.errorOutline : null,
          props.disabled ? styles.disabledInput : null,
        ]}
      >
        {leftIcon ? (
          <InputIcon
            iconKey={leftIcon.iconKey}
            iconAccessibilityLabel={leftIcon.iconAccessibilityLabel}
            onPress={leftIcon.onPress}
            iconColor={leftIcon.iconColor}
          />
        ) : null}
        <TextInput
          {...props}
          value={props.value ?? ''}
          ref={ref}
          onFocus={(e) => {
            setIsFocused(true);
            textInputAnalytics();
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          accessibilityLabel={labelText}
          accessibilityState={{
            disabled: props.disabled || props.editable === false,
          }}
          aria-invalid={isError}
          style={styles.input}
          placeholderTextColor={theme.color.text.secondary}
        />
        {rightIcon ? (
          <InputIcon
            iconKey={rightIcon.iconKey}
            iconAccessibilityLabel={rightIcon.iconAccessibilityLabel}
            iconColor={rightIcon.iconColor}
            onPress={rightIcon.onPress}
          />
        ) : null}
      </View>
      <SupportingContent
        assistiveText={assistiveText}
        errorText={errorText}
        isDisabled={props.disabled}
        onChangeText={props.onChangeText}
        suggestion={suggestion}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing.xs,
  },
  disabledSupportingText: {
    color: theme.color.interactive.disabled.text,
  },
  disabledInput: {
    backgroundColor: theme.color.interactive.disabled.background,
    borderColor: theme.color.interactive.disabled.border,
    color: theme.color.interactive.disabled.text,
  },
  errorOutline: {
    borderColor: theme.color.status.negative,
  },
  errorSupportingText: {
    color: theme.color.status.negative,
  },
  focusedOutline: {
    borderColor: theme.color.interactive.primary,
  },
  input: {
    flex: 1,
    ...theme.typography.component.input,
  },
  inputContainer: {
    alignItems: 'center',
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 12,
  },
  suggestionContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyItems: 'flex-start',
  },
  supportingRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  supportingText: {
    color: theme.color.text.secondary,
    flex: 1,
  },
});

export default VXLTextInput;
